import { ArrowLeft, ArrowRight, CheckCircle, Lock, Redo2, Trash2, Undo2 } from 'lucide-react';
import { memo, useState, type ReactNode } from 'react';

import type { CompletionStatus, NarrativeState, SegmentId } from '../types/narrative';
import { getSegmentProgress, isSegmentUnlocked } from '../utils/narrativeHelpers';

interface PlannerProps {
  variant: 'planner';
  onBack: () => void;
  onContinue: () => void;
  canContinue: boolean;
  continueLabel?: string;
  onUndo?: () => void;
  onRedo?: () => void;
  onClear?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  showBack?: boolean;
  showUndo?: boolean;
  showRedo?: boolean;
  showClear?: boolean;
  showActionsSeparator?: boolean;
  showContinue?: boolean;
  rightContent?: ReactNode;
}

interface EditorProps {
  variant: 'editor';
  onBack: () => void;
  onForward: () => void;
  canBack: boolean;
  canForward: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onClear: () => void;
  canUndo: boolean;
  canRedo: boolean;
  currentSegment: SegmentId;
  segmentCompletion: Record<SegmentId, CompletionStatus>;
  narrativeState: NarrativeState;
  onSegmentChange: (segmentId: SegmentId) => void;
  onGoToPlanner?: () => void;
}

type UnifiedBottomBarProps = PlannerProps | EditorProps;

export const UnifiedBottomBar = memo(function UnifiedBottomBar(props: UnifiedBottomBarProps) {
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showPlannerPrompt, setShowPlannerPrompt] = useState(false);

  if (props.variant === 'planner') {
    const {
      onBack,
      onContinue,
      canContinue,
      continueLabel = 'Continue',
      onUndo,
      onRedo,
      onClear,
      canUndo = false,
      canRedo = false,
      showBack = true,
      showUndo = true,
      showRedo = true,
      showClear = true,
      showActionsSeparator = true,
      showContinue = true,
      rightContent,
    } = props;
    const handleClear = () => {
      if (onClear) {
        onClear();
        setShowClearConfirm(false);
      }
    };
    return (
      <>
        <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-[#050614] to-[#0c1222] py-3 px-6 flex justify-between items-center border-t border-[#1a2d4d] shadow-[0_-4px_15px_rgba(0,0,0,0.3)] z-50">
          <div>
            {showBack && (
              <button
                onClick={onBack}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            {showUndo && (
              <button
                onClick={onUndo}
                disabled={!canUndo}
                className={`p-2 rounded-lg transition-all ${canUndo ? 'text-gray-400 hover:text-white hover:bg-[#1a2d4d]' : 'text-gray-600 cursor-not-allowed'}`}
                title="Undo last change (Ctrl+Z)"
              >
                <Undo2 className="w-4 h-4" />
              </button>
            )}
            {showRedo && (
              <button
                onClick={onRedo}
                disabled={!canRedo}
                className={`p-2 rounded-lg transition-all ${canRedo ? 'text-gray-400 hover:text-white hover:bg-[#1a2d4d]' : 'text-gray-600 cursor-not-allowed'}`}
                title="Redo last change (Ctrl+Y)"
              >
                <Redo2 className="w-4 h-4" />
              </button>
            )}
            {showClear && (
              <div className="relative">
                <button
                  onClick={() => setShowClearConfirm(true)}
                  className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-[#1a2d4d] transition-all"
                  title="Clear all fields on this page"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                {showClearConfirm && (
                  <div className="absolute bottom-full right-0 mb-2 w-64 bg-[#0c1222] border border-[#1a2d4d] rounded-lg shadow-xl p-3">
                    <p className="text-sm text-gray-300 mb-3">
                      Are you sure you want to clear all fields on this page?
                    </p>
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setShowClearConfirm(false)}
                        className="px-3 py-1 text-xs text-gray-400 hover:text-white transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleClear}
                        className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                      >
                        Clear All
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {rightContent}
            {showActionsSeparator && (showUndo || showRedo || showClear) && showContinue && (
              <div className="w-px h-6 bg-[#1a2d4d] mx-2" />
            )}
            {showContinue && (
              <button
                onClick={onContinue}
                disabled={!canContinue}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-semibold text-sm transition-all ${canContinue ? 'bg-gradient-to-r from-[#4f46e5] to-[#06b6d4] text-white hover:shadow-lg hover:scale-105' : 'bg-gray-600 text-gray-400 cursor-not-allowed'}`}
              >
                {continueLabel}
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        <div className="h-16" />
      </>
    );
  }

  const {
    onBack,
    onForward,
    canBack,
    canForward,
    onUndo,
    onRedo,
    onClear,
    canUndo,
    canRedo,
    currentSegment,
    segmentCompletion,
    narrativeState,
    onSegmentChange,
    onGoToPlanner,
  } = props as EditorProps;

  const isAtFirstNode = narrativeState.currentNodeId === 'S1_Opening_Hook';
  const handleClear = () => {
    onClear();
    setShowClearConfirm(false);
  };
  const handleBackClick = () => {
    if (isAtFirstNode) {
      setShowPlannerPrompt(true);
    } else {
      onBack();
    }
  };
  const handleGoToPlanner = () => {
    setShowPlannerPrompt(false);
    if (onGoToPlanner) onGoToPlanner();
  };

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-[#050614]/95 to-[#0c1222]/95 backdrop-blur-md py-3 px-6 flex justify-between items-center border-t border-[#1a2d4d]/50 shadow-[0_-4px_20px_rgba(0,0,0,0.4)] transition-all duration-300 z-50">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBackClick}
            className={`p-2 rounded-lg transition-all duration-300 ease-in-out ${canBack ? 'text-gray-400 hover:text-white hover:bg-[#1a2d4d]/50 hover:scale-105 hover:shadow-[0_0_8px_rgba(79,70,229,0.2)]' : isAtFirstNode ? 'text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 hover:scale-105 hover:shadow-[0_0_8px_rgba(6,182,212,0.3)]' : 'text-gray-600 cursor-not-allowed opacity-50'}`}
            title={isAtFirstNode ? 'Go to Planner' : 'Go to previous node'}
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <button
            onClick={onForward}
            disabled={!canForward}
            className={`p-2 rounded-lg transition-all duration-300 ease-in-out ${canForward ? 'text-gray-400 hover:text-white hover:bg-[#1a2d4d]/50 hover:scale-105 hover:shadow-[0_0_8px_rgba(79,70,229,0.2)]' : 'text-gray-600 cursor-not-allowed opacity-50'}`}
            title="Go to next node"
          >
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex flex-col">
            <span className="text-xs text-gray-400">Current Segment Progress</span>
            <span className="text-[10px] text-gray-500">Click to jump to another segment</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              {[1, 2, '3A', '3B'].map((segmentId) => {
                const isActive = currentSegment === segmentId;
                const unlocked = isSegmentUnlocked(narrativeState, segmentId.toString());
                const completion = segmentCompletion[segmentId as SegmentId];
                let stats = '?/?';
                let segmentName = '';
                if (segmentId === 1) {
                  segmentName = '1: Opening';
                  if (unlocked) {
                    const { complete, total } = getSegmentProgress(
                      narrativeState,
                      segmentId as SegmentId
                    );
                    stats = `${complete}/${total}`;
                  }
                } else if (segmentId === 2) {
                  segmentName = '2: Diversion';
                  if (unlocked) {
                    const { complete, total } = getSegmentProgress(
                      narrativeState,
                      segmentId as SegmentId
                    );
                    stats = `${complete}/${total}`;
                  }
                } else if (segmentId === '3A') {
                  segmentName = '3A: Good';
                  if (unlocked) {
                    const { complete, total } = getSegmentProgress(
                      narrativeState,
                      segmentId as SegmentId
                    );
                    stats = `${complete}/${total}`;
                  }
                } else if (segmentId === '3B') {
                  segmentName = '3B: Bad';
                  if (unlocked) {
                    const { complete, total } = getSegmentProgress(
                      narrativeState,
                      segmentId as SegmentId
                    );
                    stats = `${complete}/${total}`;
                  }
                }
                return (
                  <button
                    key={segmentId}
                    onClick={() => unlocked && onSegmentChange(segmentId as SegmentId)}
                    disabled={!unlocked}
                    className={`px-2 py-1 text-xs rounded transition-all duration-300 ease-in-out ${unlocked ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'} ${isActive ? (completion === 'complete' ? 'bg-green-900/30 text-green-400 border border-green-500/30' : completion === 'partial' ? 'bg-blue-900/30 text-blue-400 border border-blue-500/30' : 'bg-red-900/30 text-red-400 border border-red-500/30') : unlocked ? (completion === 'complete' ? 'text-green-500 hover:bg-green-900/20' : completion === 'partial' ? 'text-blue-500 hover:bg-blue-900/20' : 'text-red-500 hover:bg-red-900/20') : 'text-gray-600'} ${unlocked ? 'hover:scale-105' : ''}`}
                    title={`${segmentName} - ${stats}`}
                  >
                    <div className="flex flex-col items-center gap-0.5">
                      <div className="flex items-center gap-1">
                        {!unlocked ? (
                          <Lock className="w-3 h-3" />
                        ) : completion === 'complete' ? (
                          <CheckCircle className="w-3 h-3" />
                        ) : null}
                        <span className="font-medium">{segmentName}</span>
                      </div>
                      <span className="text-[10px] opacity-75">{stats}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onUndo}
            disabled={!canUndo}
            className={`p-2 rounded-lg transition-all duration-300 ease-in-out ${canUndo ? 'text-gray-400 hover:text-white hover:bg-[#1a2d4d]/50 hover:scale-105 hover:shadow-[0_0_8px_rgba(6,182,212,0.2)]' : 'text-gray-600 cursor-not-allowed opacity-50'}`}
            title="Undo last change (Ctrl+Z)"
          >
            <Undo2 className="w-4 h-4" />
          </button>
          <button
            onClick={onRedo}
            disabled={!canRedo}
            className={`p-2 rounded-lg transition-all duration-300 ease-in-out ${canRedo ? 'text-gray-400 hover:text-white hover:bg-[#1a2d4d]/50 hover:scale-105 hover:shadow-[0_0_8px_rgba(6,182,212,0.2)]' : 'text-gray-600 cursor-not-allowed opacity-50'}`}
            title="Redo last change (Ctrl+Y)"
          >
            <Redo2 className="w-4 h-4" />
          </button>
          <div className="relative">
            <button
              onClick={() => setShowClearConfirm(true)}
              className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-900/20 hover:scale-105 hover:shadow-[0_0_8px_rgba(239,68,68,0.3)] transition-all duration-300 ease-in-out"
              title="Clear all fields on this node"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            {showClearConfirm && (
              <div className="absolute bottom-full right-0 mb-2 w-64 bg-[#0c1222]/95 backdrop-blur-md border border-[#1a2d4d]/70 rounded-lg shadow-[0_8px_32px_rgba(0,0,0,0.4)] p-3">
                <p className="text-sm text-gray-300 mb-3">
                  Are you sure you want to clear all fields for this node?
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setShowClearConfirm(false)}
                    className="px-3 py-1 text-xs text-gray-400 hover:text-white hover:bg-gray-700/30 rounded transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleClear}
                    className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 hover:scale-105 hover:shadow-[0_0_10px_rgba(239,68,68,0.4)] text-white rounded transition-all duration-200"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {showPlannerPrompt && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100]">
          <div className="bg-[#0c1222]/95 backdrop-blur-md border border-[#1a2d4d]/70 rounded-lg shadow-[0_8px_32px_rgba(0,0,0,0.4)] p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-white mb-3">Edit Your Plan</h3>
            <p className="text-sm text-gray-300 mb-6 leading-relaxed">
              In Editor Mode, you can use the{' '}
              <span className="text-blue-400 font-medium">Planner Tab</span> to make changes to your
              plan. This will take you back to the planning interface while preserving your current
              progress.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowPlannerPrompt(false)}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white hover:bg-gray-700/30 rounded transition-all duration-200"
              >
                Back
              </button>
              <button
                onClick={handleGoToPlanner}
                className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 hover:scale-105 hover:shadow-[0_0_10px_rgba(6,182,212,0.4)] text-white rounded transition-all duration-200 font-medium"
              >
                Go to Planner Tab
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="h-20" />
    </>
  );
});
